import { TrashIcon } from "vega-icons";

import {
  Accordion,
  AccordionVariant,
} from "@vega-core/components/Accordion/Accordion";
import {
  CardVariant,
  EmptyCard,
} from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  type BaseMultiSelectOption,
  ThemedMultiselectDropdown,
} from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { Switch } from "@vega-core/components/Switch/Switch";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { FundLogos } from "@vega-modules/components/AssetClassLogos/AssetClassImages";

import { type CollaboratorsState } from "./InviteCollaboratorsDialog";

type CollaboratorsProps = {
  collaboratorsAccordion: CollaboratorsState;
  handleApplyAllFundsToggle: (id: number) => void;
  deleteCollaboratorsAccordion: (id: number) => void;
  fundOptions: OptionType[];
  rolesOptions: BaseMultiSelectOption[];
  handleFundSelectionChange: (
    id: number,
    selectedFunds: BaseMultiSelectOption[],
  ) => void;
  handleRoleChange: (id: number, selectedRole: BaseMultiSelectOption) => void;
  fundsSearchQuery?: string;
  onFundsSearchChange?: (value: string) => void;
  onFundsLoadMore?: () => void;
  fundsHasNextPage?: boolean;
  fundsIsLoadingMore?: boolean;
};

export const Collaborators = ({
  collaboratorsAccordion: {
    id,
    index,
    selectedRole,
    applyAllFund,
    selectedFunds,
  },
  handleApplyAllFundsToggle,
  deleteCollaboratorsAccordion,
  fundOptions,
  rolesOptions,
  handleFundSelectionChange,
  handleRoleChange,
  fundsSearchQuery,
  onFundsSearchChange,
  onFundsLoadMore,
  fundsHasNextPage,
  fundsIsLoadingMore,
}: CollaboratorsProps) => {
  const accordionItem = {
    value: String(id),
    title: (
      <div className="flex items-start w-full justify-between">
        <span className="heading-6 text-text/secondary">
          {selectedRole.label ||
            m.gp_subscription_tab_tasks_contactMatrix_contact_role({
              index,
            })}
        </span>
        {index > 1 && (
          <div className="px-4">
            <TrashIcon
              className="text-text/secondary w-4 h-4 cursor-pointer "
              onClick={() => deleteCollaboratorsAccordion(id)}
            />
          </div>
        )}
      </div>
    ),
    content: (
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        <ThemedDropdown
          isClearable={false}
          name="collaboratorsRoles"
          onChange={(newSelectedRole) => {
            const fullRole = rolesOptions.find(
              (role) => role.value === newSelectedRole?.value,
            );
            handleRoleChange(id, fullRole ?? { label: "", value: "", id: "" });
          }}
          options={rolesOptions}
          placeholder={m.settings_collaborators_inviteCollaboratorSelectRole()}
          value={selectedRole.value}
        />
        <div className="flex items-center justify-between">
          <span className="body-base-medium">
            {m.settings_collaborators_inviteCollaboratorApplyAllFunds()}
          </span>
          <Switch
            checked={applyAllFund}
            id={String(id)}
            onChange={() => {
              handleApplyAllFundsToggle(id);
            }}
          />
        </div>
        {!applyAllFund && (
          <ThemedMultiselectDropdown
            emptySearchElement={
              <EmptyCard
                description={m.settings_collaborators_inviteCollaboratorNoFundsFoundDescription()}
                title={m.settings_collaborators_inviteCollaboratorNoFundsFound()}
                variant={CardVariant.SearchUser}
              />
            }
            hasNextPage={fundsHasNextPage}
            isLoadingMore={fundsIsLoadingMore}
            name={`${id}.funds`}
            onLoadMore={onFundsLoadMore}
            onSearchChange={onFundsSearchChange}
            options={fundOptions}
            placeholder={m.settings_collaborators_inviteCollaboratorSelectFunds()}
            renderOption={(option) => (
              <>
                <div className="flex-shrink-0 w-8 h-8">
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <FundLogos assetClass={(option as any).assetValue} />
                </div>
                <span className="capitalize">{option.label}</span>
              </>
            )}
            searchEnabled
            searchThreshold={0}
            searchValue={fundsSearchQuery}
            selectedOptions={selectedFunds}
            setSelectedOptions={(newSelectedFunds) => {
              handleFundSelectionChange(id, newSelectedFunds);
            }}
            shouldFilter={false}
          />
        )}
        {selectedFunds.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex body-small-semibold text-text/secondary">
              {m.settings_collaborators_inviteCollaboratorSelectedFunds()}
            </div>
            <div className="flex flex-col gap-3 max-h-32 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-border/secondary [&::-webkit-scrollbar-track]:bg-transparent">
              {selectedFunds.map((fund) => (
                <div className="flex items-center gap-3" key={fund.value}>
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  <FundLogos assetClass={(fund as any).assetValue} />
                  <span className="body-base-medium">{fund.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    ),
  };

  return (
    <div className="rounded-lg border border-border/secondary">
      <Accordion
        collapsible
        defaultValue={String(id)}
        items={[accordionItem]}
        type="single"
        variant={AccordionVariant.CONTENT_WITHOUT_BG}
      />
    </div>
  );
};
